import React, { useEffect, useState } from 'react';
import { useAuth } from '../AuthContext';
import { supabase } from '../supabaseClient';
import { Link } from 'react-router-dom';

const STATUS_OPTIONS = ['New', 'In Progress', 'Completed'];

const AdminDashboard = () => {
  const { user } = useAuth();
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quoteRequests, setQuoteRequests] = useState([]);
  const [fetchError, setFetchError] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);

  const fetchQuoteRequests = async () => {
    if (user && role === 'admin') {
      const { data, error } = await supabase
        .from('quote_requests')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) {
        setFetchError('Failed to fetch quote requests.');
        setQuoteRequests([]);
      } else {
        setQuoteRequests(data || []);
        setFetchError(null);
      }
    }
  };

  useEffect(() => {
    const fetchRoleAndRequests = async () => {
      if (user) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('role')
          .eq('id', user.id)
          .single();
        setRole(profile?.role || null);
        if (profile?.role === 'admin') {
          await fetchQuoteRequests();
        }
      }
      setLoading(false);
    };
    fetchRoleAndRequests();
    // eslint-disable-next-line
  }, [user]);

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this quote request?')) return;
    setDeletingId(id);
    const { error } = await supabase
      .from('quote_requests')
      .delete()
      .eq('id', id);
    setDeletingId(null);
    if (error) {
      alert('Failed to delete quote request.');
    } else {
      await fetchQuoteRequests();
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    setUpdatingId(id);
    const { error } = await supabase
      .from('quote_requests')
      .update({ status: newStatus })
      .eq('id', id);
    setUpdatingId(null);
    if (error) {
      alert('Failed to update status.');
    } else {
      await fetchQuoteRequests();
    }
  };

  if (loading) return <div className="text-center mt-10">Loading...</div>;
  if (!user) return <div className="text-center mt-10">You must be logged in to view this page.</div>;
  if (role !== 'admin') return <div className="text-center mt-10">Access Denied: Admins only.</div>;

  return (
    <div className="p-8 max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold mb-4">Admin Dashboard</h1>
      <div className="mb-6">
        <Link to="/analytics">
          <button className="bg-accent text-white px-4 py-2 rounded font-semibold hover:bg-primary transition">
            View Analytics Dashboard
          </button>
        </Link>
      </div>
      <h2 className="text-xl font-semibold mb-2">Quote Requests</h2>
      {fetchError && <div className="text-red-500 mb-4">{fetchError}</div>}
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white rounded-xl shadow-soft">
          <thead>
            <tr>
              <th className="px-4 py-2">Name</th>
              <th className="px-4 py-2">Email</th>
              <th className="px-4 py-2">Message</th>
              <th className="px-4 py-2">Status</th>
              <th className="px-4 py-2">Created At</th>
              <th className="px-4 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {quoteRequests.map((req) => (
              <tr key={req.id} className="border-t">
                <td className="px-4 py-2">{req.name}</td>
                <td className="px-4 py-2">{req.email}</td>
                <td className="px-4 py-2">{req.message}</td>
                <td className="px-4 py-2">
                  <select
                    value={req.status}
                    onChange={e => handleStatusChange(req.id, e.target.value)}
                    disabled={updatingId === req.id}
                    className="border rounded px-2 py-1"
                  >
                    {STATUS_OPTIONS.map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                  {updatingId === req.id && <span className="ml-2 text-xs text-gray-500">Updating...</span>}
                </td>
                <td className="px-4 py-2">{new Date(req.created_at).toLocaleString()}</td>
                <td className="px-4 py-2">
                  <button
                    onClick={() => handleDelete(req.id)}
                    className="text-red-500 hover:underline text-sm"
                    disabled={deletingId === req.id}
                  >
                    {deletingId === req.id ? 'Deleting...' : 'Delete'}
                  </button>
                </td>
              </tr>
            ))}
            {quoteRequests.length === 0 && (
              <tr>
                <td colSpan={6} className="text-center py-4 text-gray-500">
                  No quote requests found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminDashboard; 